import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { VideoIcon, VideoOffIcon, MicOffIcon, PipIcon, MaximizeIcon, MinimizeIcon, SwapIcon } from './Icons.jsx';

/**
 * @typedef {object} VideoWindowProps
 * @property {MediaStream} [stream]
 * @property {string} label
 * @property {boolean} [muted=false]
 * @property {boolean} [mirror=false]
 * @property {boolean} [videoOn=true]
 * @property {boolean} [isPip=false]
 * @property {string} [aspect='default'] - 'fill' | 'video' | 'default' | 'pip'
 * @property {() => void} [onSwap]
 * @property {() => void} [onPip]
 * @property {() => void} [onToggleFullscreen]
 * @property {boolean} [isFullscreen=false]
 * @property {string} [className='']
 */
const VideoWindow = forwardRef(function VideoWindow(
  {
    stream,
    label,
    muted = false,
    mirror = false,
    videoOn = true,
    isPip = false,
    aspect = 'default',
    onSwap,
    onPip,
    onToggleFullscreen,
    isFullscreen = false,
    className = '',
  },
  forwardedRef
) {
  const videoRef = useRef(/** @type {HTMLVideoElement|null} */ (null));
  const containerRef = useRef(/** @type {HTMLDivElement|null} */ (null));
  const [ready, setReady] = useState(false);
  const [hasVideoTrack, setHasVideoTrack] = useState(true);

  // Expose the video DOM element to parent (for PiP / fullscreen)
  useImperativeHandle(forwardedRef, () => videoRef.current);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    if (stream) {
      el.srcObject = stream;
      const vt = stream.getVideoTracks();
      setHasVideoTrack(vt.length > 0 && vt[0].enabled);

      const p = el.play();
      if (p) {
        p.then(() => setReady(true)).catch(() => {
          // Autoplay policy fallback: muted video always plays
          el.muted = true;
          el.play().then(() => setReady(true)).catch(() => {});
        });
      }
    } else {
      el.srcObject = null;
      setReady(false);
      setHasVideoTrack(false);
    }

    return () => {
      if (el) el.srcObject = null;
      setReady(false);
    };
  }, [stream]);

  // Monitor video track enabled changes
  useEffect(() => {
    if (!stream) return;
    const checkTracks = () => {
      const vt = stream.getVideoTracks();
      setHasVideoTrack(vt.length > 0 && vt[0].enabled && videoOn);
    };
    checkTracks();
    const interval = setInterval(checkTracks, 500);
    return () => clearInterval(interval);
  }, [stream, videoOn]);

  const showVideo = ready && hasVideoTrack && videoOn;
  const initials = (label || 'U')
    .replace(/[()]/g, '')
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  // Container styling depending on mode
  let aspectClass = 'aspect-video max-h-96';
  if (aspect === 'fill') {
    aspectClass = 'w-full h-full';
  } else if (aspect === 'pip' || isPip) {
    aspectClass = 'w-full h-full';
  } else if (aspect === 'square') {
    aspectClass = 'aspect-square';
  }

  return (
    <div
      ref={containerRef}
      onClick={isPip && onSwap ? onSwap : undefined}
      className={`group relative overflow-hidden bg-gray-950 select-none ${
        isPip
          ? 'rounded-2xl cursor-pointer shadow-2xl ring-2 ring-white/20 hover:ring-primary-500 hover:scale-[1.03] active:scale-95 transition-all duration-200'
          : 'rounded-2xl border border-white/10 shadow-lg'
      } ${aspectClass} ${className}`}
    >
      {/* Video Element */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        webkit-playsinline="true"
        x5-playsinline="true"
        muted={muted}
        style={{
          transform: mirror ? 'scaleX(-1)' : 'none',
        }}
        className={`w-full h-full object-cover transition-opacity duration-300 ${
          showVideo ? 'opacity-100' : 'opacity-0 absolute inset-0 pointer-events-none'
        }`}
      />

      {/* Camera Off / Loading Avatar Fallback */}
      {!showVideo && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-gray-900 to-gray-950 p-4">
          <div className="relative mb-2">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-tr from-primary-700 to-primary-500 flex items-center justify-center text-white font-bold text-xl sm:text-2xl shadow-xl ring-4 ring-white/10">
              {initials}
            </div>
            {!videoOn && (
              <span className="absolute bottom-0 right-0 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center ring-2 ring-gray-900 shadow">
                <VideoOffIcon size={12} />
              </span>
            )}
          </div>
          <span className="text-xs font-medium text-gray-400 text-center max-w-[140px] truncate">
            {!videoOn ? 'Камера выключена' : ready ? label : 'Подключение видео…'}
          </span>
        </div>
      )}

      {/* Swap Hint Badge for PiP overlay */}
      {isPip && (
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white gap-1.5 backdrop-blur-[2px]">
          <span className="bg-black/60 rounded-full p-2 text-white shadow-md">
            <SwapIcon size={18} />
          </span>
        </div>
      )}

      {/* Floating Action Overlay on Hover/Touch (PiP & Fullscreen) */}
      {!isPip && (
        <div className="absolute top-3 right-3 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 sm:focus-within:opacity-100 transition-opacity duration-200 z-10">
          {onPip && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onPip();
              }}
              title="Картинка в картинке"
              className="p-1.5 rounded-lg bg-black/60 hover:bg-black/80 text-white/90 hover:text-white border border-white/10 backdrop-blur-md transition-colors"
            >
              <PipIcon size={15} />
            </button>
          )}
          {onToggleFullscreen && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleFullscreen();
              }}
              title={isFullscreen ? 'Свернуть' : 'Во весь экран'}
              className="p-1.5 rounded-lg bg-black/60 hover:bg-black/80 text-white/90 hover:text-white border border-white/10 backdrop-blur-md transition-colors"
            >
              {isFullscreen ? <MinimizeIcon size={15} /> : <MaximizeIcon size={15} />}
            </button>
          )}
        </div>
      )}

      {/* Label and Audio Status Bar */}
      <div className={`absolute bottom-0 left-0 right-0 ${isPip ? 'p-1.5' : 'p-3'} bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-center justify-between pointer-events-none z-10`}>
        <div className="flex items-center gap-1.5 min-w-0">
          <span className={`font-medium text-white/95 truncate drop-shadow-md ${isPip ? 'text-[10px] px-1 py-0.5 bg-black/60 rounded backdrop-blur-sm' : 'text-xs sm:text-sm'}`}>
            {label}
          </span>
          {muted && (
            <span className="text-red-400 drop-shadow" title="Звук отключен">
              <MicOffIcon size={isPip ? 11 : 14} />
            </span>
          )}
        </div>
      </div>
    </div>
  );
});

export default VideoWindow;